import { REMOTE_FLAG_KEY } from './flags'

/**
 * remote-flag.js — KILL SWITCH REMOTO de Tareas offline (apagado de emergencia sin release).
 *
 * POR QUÉ EXISTE: las envs (OFFLINE_MODE_ENABLED) se inlinean en tiempo de BUILD, así que apagar Tareas
 * offline en un móvil YA INSTALADO exigiría publicar una APK nueva. Con la bandera ON por defecto para todo
 * el fleet, eso no es aceptable: si Tareas offline falla en campo hay que poder apagarlo HOY. Esto lo
 * resuelve leyendo el flag del server y cacheándolo; flags.js consume el cache (ver su precedencia).
 *
 * DÓNDE VIVE EL FLAG EN EL BACK: en el blob `extra_settings` de la organización, que YA existe y ya es
 * de forma libre — no hizo falta ningún cambio de backend:
 *   GET  organizations/{orgId}/config/?type=extra_settings   (GeneralSettingController@settings;
 *        auto-crea la fila con defaults si no está, así que siempre responde)
 *   POST organizations/settings/{orgId}/extra-settings  { data: [{ ..., offline_mode_enabled: false }] }
 *        (OrganizationSettingsController@updateExtraSettings — acepta el blob sin whitelist)
 * El blob es un ARRAY cuyo [0] es el objeto de settings (GeneralSetting::defaultExtraSettings()).
 * AUSENTE = no opina -> la bandera sigue con su default (ON). Solo un `false`/`true` EXPLÍCITO decide.
 *
 * REGLAS QUE NO SON NEGOCIABLES:
 *  - Este refresh NO se gatea por `offline_mode_enabled`. Si se gateara, apagar la bandera desde el back
 *    dejaría de refrescarla y ya NUNCA podrías volver a encenderla: el apagado sería un billete de ida.
 *  - Best-effort y silencioso: sin red / 401 / 500 -> se CONSERVA el último valor cacheado. Un fallo de
 *    red NO debe encender ni apagar nada por sí solo.
 */

/** Lee el flag del blob de extra_settings. undefined = la llave no viene = el server no opina. */
export function parseRemoteOfflineFlag (raw) {
  let blob = raw
  // El endpoint devuelve `data` como STRING JSON (así lo parsea también stores/organization/actions.js).
  if (typeof blob === 'string') {
    try { blob = JSON.parse(blob) } catch { return undefined }
  }
  // defaultExtraSettings() devuelve [{...}]: el objeto de settings es el [0].
  const settings = Array.isArray(blob) ? blob[0] : blob
  if (!settings || typeof settings !== 'object') return undefined
  const v = settings.offline_mode_enabled
  if (v === true || v === false) return v
  // Tolera 'true'/'false' por si el admin lo guarda como texto.
  if (v === 'true') return true
  if (v === 'false') return false
  return undefined // ausente o basura -> no opina
}

/** Guarda el valor conocido (o lo borra si el server dejó de opinar). */
function cacheRemoteOfflineFlag (value) {
  try {
    if (value === undefined) localStorage.removeItem(REMOTE_FLAG_KEY)
    else localStorage.setItem(REMOTE_FLAG_KEY, value ? 'true' : 'false')
  } catch { /* sin localStorage: no-op */ }
}

/**
 * Refresca el kill switch remoto desde el server. NUNCA lanza y NUNCA se gatea por el flag.
 *
 * @param {number|string} orgId  organización del usuario autenticado.
 * @param {{ fetchSettings?: Function }} deps  costura para tests (default: el service real).
 * @returns {Promise<boolean|undefined>} el valor aplicado, o undefined si el server no opina/falló.
 */
export async function refreshRemoteOfflineFlag (orgId, { fetchSettings } = {}) {
  if (orgId == null) return undefined
  try {
    const get = fetchSettings || (async (id) => {
      // Import dinámico: este módulo lo carga el boot al arrancar, y no queremos arrastrar el grafo de
      // servicios cuando no hay org/red.
      const { default: OrganizationService } = await import('src/services/organization')
      return OrganizationService.getGeneralSettings(id, 'extra_settings')
    })
    const res = await get(orgId)
    const rawBlob = res && res.data ? res.data.data : undefined
    const value = parseRemoteOfflineFlag(rawBlob)
    cacheRemoteOfflineFlag(value)
    return value
  } catch {
    // Sin red / error: se conserva el último valor cacheado (ver cabecera).
    return undefined
  }
}
